import {Button, SkeletonBox} from '@primer/react'
import {CopilotIcon, FileCheckIcon, TriangleDownIcon} from '@primer/octicons-react'
import {IconButtonSelector} from './task-chat-input-toolbar/IconButtonSelector'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {clsx} from 'clsx'
import {useCallback} from 'react'
import type {SubAgent} from '@github-ui/agent-sessions-types/sub-agent'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import styles from './AgentSelector.module.css'

type AnchorInjectedProps = {
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup']
  'aria-expanded'?: React.AriaAttributes['aria-expanded']
  id?: string
}

export type AgentSelectorTriggerVisual =
  | {kind: 'loading'}
  | {kind: 'icon'; icon: typeof CopilotIcon; selected: boolean}
  | {kind: 'avatar'; src: string}

export type AgentSelectorTriggerRenderProps = {
  visual: AgentSelectorTriggerVisual
  ariaLabel: string
  disabled: boolean
  className?: string
  ref?: React.Ref<HTMLButtonElement>
} & AnchorInjectedProps

export type AgentSelectorTriggerComponent = React.ComponentType<AgentSelectorTriggerRenderProps>

type AgentSelectorMenuTriggerProps = {
  isLoading: boolean
  isCopilotSelected: boolean
  selectedAgentObj: SubAgent | AgentInfo | undefined
  agentType: string | undefined
  ariaLabel: string
  disabled: boolean
  className?: string
  triggerComponent?: AgentSelectorTriggerComponent
  ref?: React.Ref<HTMLButtonElement>
} & AnchorInjectedProps

function AgentSelectorTriggerVisualContent({visual, size}: {visual: AgentSelectorTriggerVisual; size: number}) {
  if (visual.kind === 'loading') {
    return (
      <span className={styles.loadingVisual}>
        <SkeletonBox width={size} height={size} aria-hidden="true" data-testid="agent-selector-skeleton" />
      </span>
    )
  }

  if (visual.kind === 'avatar') {
    return <GitHubAvatar src={visual.src} size={size} square />
  }

  const Icon = visual.icon
  return <Icon size={size} />
}

function preventMenuOpen(event: React.MouseEvent<HTMLButtonElement>) {
  event.preventDefault()
  event.stopPropagation()
}

export function AgentSelectorIconTrigger({
  visual,
  ariaLabel,
  disabled,
  className,
  ref,
  onClick,
  onKeyDown,
  ...anchorProps
}: AgentSelectorTriggerRenderProps) {
  const isLoading = visual.kind === 'loading'
  const TriggerVisual = useCallback(
    () => <AgentSelectorTriggerVisualContent visual={visual} size={visual.kind === 'avatar' ? 18 : 16} />,
    [visual],
  )

  return (
    <IconButtonSelector
      {...anchorProps}
      icon={TriggerVisual}
      selectedIcon={TriggerVisual}
      aria-label={ariaLabel}
      aria-disabled={isLoading || undefined}
      aria-busy={isLoading || undefined}
      selected={visual.kind === 'icon' && visual.selected}
      disabled={disabled}
      inactive={isLoading || undefined}
      variant="invisible"
      size="medium"
      className={clsx(visual.kind === 'avatar' && styles.avatarButton, className)}
      ref={ref}
      onClick={isLoading ? preventMenuOpen : onClick}
      onKeyDown={isLoading ? undefined : onKeyDown}
    />
  )
}

export function AgentSelectorCaretTrigger({
  visual,
  ariaLabel,
  disabled,
  className,
  ref,
  onClick,
  onKeyDown,
  ...anchorProps
}: AgentSelectorTriggerRenderProps) {
  const isLoading = visual.kind === 'loading'

  return (
    <Button
      {...anchorProps}
      type="button"
      trailingAction={TriangleDownIcon}
      aria-label={ariaLabel}
      aria-disabled={isLoading || undefined}
      disabled={disabled}
      inactive={isLoading || undefined}
      variant="invisible"
      size="medium"
      className={clsx(styles.caretTrigger, className)}
      ref={ref}
      onClick={isLoading ? preventMenuOpen : onClick}
      onKeyDown={isLoading ? undefined : onKeyDown}
    >
      <span className={styles.triggerIcon}>
        <AgentSelectorTriggerVisualContent visual={visual} size={16} />
      </span>
    </Button>
  )
}

export function AgentSelectorMenuTrigger({
  isLoading,
  isCopilotSelected,
  selectedAgentObj,
  agentType,
  ariaLabel,
  disabled,
  className,
  triggerComponent: TriggerComponent = AgentSelectorIconTrigger,
  ref,
  ...anchorProps
}: AgentSelectorMenuTriggerProps) {
  const visual: AgentSelectorTriggerVisual = (() => {
    if (isLoading) return {kind: 'loading'}
    if (isCopilotSelected) return {kind: 'icon', icon: CopilotIcon, selected: false}
    if (selectedAgentObj && typeof selectedAgentObj === 'object' && agentType === 'customSubAgent') {
      return {kind: 'icon', icon: FileCheckIcon, selected: true}
    }
    if (selectedAgentObj && typeof selectedAgentObj === 'object' && 'avatar_url' in selectedAgentObj) {
      return {kind: 'avatar', src: selectedAgentObj.avatar_url}
    }
    return selectedAgentObj
      ? {kind: 'icon', icon: FileCheckIcon, selected: true}
      : {kind: 'icon', icon: CopilotIcon, selected: false}
  })()

  return (
    <TriggerComponent
      visual={visual}
      ariaLabel={isLoading ? 'Loading agents' : ariaLabel}
      disabled={disabled}
      className={className}
      ref={ref}
      {...anchorProps}
    />
  )
}
